/**
 * features/expenses/SupplierPicker.tsx — เลือกผู้ขายสำหรับฟอร์มค่าใช้จ่าย
 *
 * โครงเดียวกับ CustomerPicker ของฟอร์มเอกสาร ต่างกันที่ผูกกับ supplier
 */

import { useState } from 'react';
import { useDebounced } from '~/lib/use-debounced';
import { Combobox } from '~/ui/Combobox';
import { Field } from '~/ui/primitives';
import { TaxIdLookupField } from '~/features/tax-lookup/TaxIdLookupField';
import { useSupplierSearch } from '~/features/suppliers/use-supplier-search';

interface SupplierPickerProps {
  supplierId: string | null;
  supplierName: string;
  taxId: string;
  onChange: (patch: { supplierId?: string | null; supplierName?: string; taxId?: string }) => void;
}

export function SupplierPicker({ supplierId, supplierName, taxId, onChange }: SupplierPickerProps) {
  const [nameInput, setNameInput] = useState(supplierName);
  const debouncedName = useDebounced(nameInput);
  const { data, isFetching } = useSupplierSearch(debouncedName);

  return (
    <div className="space-y-3">
      <Field label="ผู้ขาย/คู่ค้า">
        <Combobox
          value={nameInput}
          onChange={(value) => {
            setNameInput(value);
            onChange({ supplierName: value, supplierId: null });
          }}
          onSelect={(supplier) => {
            setNameInput(supplier.name);
            onChange({ supplierId: supplier.id, supplierName: supplier.name, taxId: supplier.taxId });
          }}
          options={data?.data ?? []}
          isLoading={isFetching}
          getLabel={(supplier) => supplier.name}
          getSublabel={(supplier) => supplier.phone}
          placeholder="ค้นหาผู้ขายที่มีอยู่ หรือพิมพ์ชื่อใหม่"
        />
      </Field>

      <TaxIdLookupField
        value={taxId}
        onChange={(value) => onChange({ taxId: value })}
        onFound={(branch) => {
          onChange({
            taxId: branch.taxId,
            supplierName: supplierName || branch.name,
          });
          setNameInput(supplierName || branch.name);
        }}
      />

      {supplierId ? null : (
        <p className="text-xs text-fg-muted">
          ผู้ขายรายนี้ยังไม่มีในระบบ — ระบบจะบันทึกเป็นชื่อผู้ขายไว้กับรายการนี้เท่านั้น
        </p>
      )}
    </div>
  );
}
